import { QueryParams } from '@/lib/api/types';
import { appendQueryParams, isAbsoluteUrl, normalizePath } from '@/lib/utils/url.utils';
import { getAuthToken, useAuthStore } from '@/store/auth.store';
import { getRuntimeConfig } from '@/lib/config/runtime';
import { isDebugPanelEnabled } from '@/lib/debug/enabled';
import { useDebugStore } from '@/store/debug.store';
import { redact } from '@/lib/debug/redact';
import { randomUUID } from '@/lib/uuid';
import { normalizeApiError, normalizeNetworkError } from '@/lib/api/normalizeApiError';

const AUTH_EXCLUDED_PATHS = ['/auth/login', '/auth/signup', '/auth/refresh'];

export type ApiMode = 'mock' | 'real' | 'auto';

// Cache del modo efectivo para evitar re-detección constante
let cachedEffectiveMode: 'mock' | 'real' | null = null;

type ApiRequestInit = RequestInit & {
  params?: QueryParams;
};

let refreshPromise: Promise<{ token: string; refreshToken: string } | null> | null = null;
let logoutInProgress = false;

const refreshSession = async (): Promise<{ token: string; refreshToken: string } | null> => {
  if (!refreshPromise) {
    refreshPromise = apiFetch<{ token: string; refreshToken: string }>('/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    })
      .then(response => response)
      .catch(err => {
        console.error('[API] Refresh token failed:', err);
        return null;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

/**
 * Detecta si estamos en un entorno con restricciones de red (iframe cross-origin, sandbox, etc.)
 */
const isRestrictedEnvironment = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    // Verificar si estamos en un iframe
    const inIframe = window.self !== window.top;

    if (!inIframe) {
      return false;
    }

    // Si FORCE_MOCK_IN_IFRAME está habilitado, forzar detección
    const { VITE_FORCE_MOCK_IN_IFRAME } = getRuntimeConfig();
    if (VITE_FORCE_MOCK_IN_IFRAME === 'true') {
      return true;
    }

    // Intentar acceder al parent - si falla, es cross-origin
    try {
      // Intentar acceder a una propiedad del parent
      void window.top?.location.href;
      return false; // Same-origin iframe, no hay restricciones
    } catch {
      return true; // Cross-origin iframe, hay restricciones CORS
    }
  } catch {
    return true; // Error de acceso = entorno restrictivo
  }
};

/**
 * Obtiene el modo API efectivo considerando configuración y entorno
 */
const getApiMode = (): 'mock' | 'real' => {
  // Retornar modo cacheado si ya fue determinado
  if (cachedEffectiveMode) {
    return cachedEffectiveMode;
  }

  const { VITE_API_MODE } = getRuntimeConfig();
  const configuredMode = VITE_API_MODE || 'auto';

  // Si está explícitamente configurado como mock o real, respetar esa configuración
  if (configuredMode === 'mock') {
    cachedEffectiveMode = 'mock';
    // eslint-disable-next-line no-console
    console.info('[API] Using mock mode (configured)');
    return 'mock';
  }

  if (configuredMode === 'real') {
    cachedEffectiveMode = 'real';
    // eslint-disable-next-line no-console
    console.info('[API] Using real mode (configured)');
    return 'real';
  }

  // Modo auto: detectar entorno y decidir
  const isRestricted = isRestrictedEnvironment();

  if (isRestricted) {
    cachedEffectiveMode = 'mock';
    // eslint-disable-next-line no-console
    console.info('[API] Using mock mode (detected restricted environment - iframe/sandbox)');
    return 'mock';
  }

  // Por defecto, usar mock en desarrollo
  cachedEffectiveMode = 'mock';
  // eslint-disable-next-line no-console
  console.info('[API] Using mock mode (default)');
  return 'mock';
};

/**
 * Resetea el modo cacheado (útil para testing o cambios dinámicos)
 */
export const resetApiMode = () => {
  cachedEffectiveMode = null;
};

const resolveBaseUrl = (mode: ApiMode): string => {
  const { VITE_API_BASE_URL } = getRuntimeConfig();
  const baseUrl = VITE_API_BASE_URL;

  if (!baseUrl) {
    console.error(
      '[API] Configuration Error: VITE_API_BASE_URL is missing. Check public/runtime-config.js or environment variables.',
    );
    throw new Error(
      'API Configuration Error: VITE_API_BASE_URL is required. Please check your runtime configuration.',
    );
  }

  if (mode === 'mock') {
    return `${baseUrl.replace(/\/$/, '')}/api/mock`;
  }

  return baseUrl.replace(/\/$/, '');
};

const buildRelativeUrl = (base: string, path: string, params: QueryParams): URL => {
  const normalizedPath = normalizePath(path);

  const url = base.startsWith('http')
    ? new URL(base + normalizedPath)
    : new URL(`${base}${normalizedPath}`, 'http://localhost');

  appendQueryParams(url, params);

  return url;
};

const buildUrl = (path: string, params: QueryParams = {}): string => {
  const mode = getApiMode();

  // Absolute URL → use as-is
  if (isAbsoluteUrl(path)) {
    const url = new URL(path);
    appendQueryParams(url, params);
    return url.toString();
  }

  const base = resolveBaseUrl(mode);
  const url = buildRelativeUrl(base, path, params);

  return url.toString();
};

const buildHeaders = ({
  headers,
  isAuthRequest,
  isFormData,
}: {
  headers?: HeadersInit;
  isAuthRequest: boolean;
  isFormData: boolean;
}): Headers => {
  const token = getAuthToken();
  const merged = new Headers(headers);

  if (!isFormData && !merged.has('Content-Type')) {
    merged.set('Content-Type', 'application/json');
  }

  if (token && !isAuthRequest) {
    merged.set('Authorization', `Bearer ${token}`);
  }

  return merged;
};

const request = async (path: string, init: ApiRequestInit = {}): Promise<Response> => {
  if (logoutInProgress) {
    throw {
      name: 'ApiError',
      status: 401,
      code: 'SESSION_EXPIRED',
      message: 'La sesión ha caducado.',
    };
  }

  const { params, headers, ...rest } = init;

  const url = buildUrl(path, params);
  const isAuthRequest = AUTH_EXCLUDED_PATHS.some(authPath => url.includes(authPath));
  const isFormData = rest.body instanceof FormData;

  const doFetch = async () => {
    try {
      return await fetch(url, {
        ...rest,
        credentials: 'include',
        headers: buildHeaders({ headers, isAuthRequest, isFormData }),
      });
    } catch (error) {
      const networkError = error as Error;

      console.error('[API] Network error:', {
        url,
        error: networkError.message,
        mode: getApiMode(),
        isRestrictedEnv: isRestrictedEnvironment(),
      });

      if (isDebugPanelEnabled()) {
        useDebugStore.getState().push({
          type: 'network-error',
          id: randomUUID(),
          timestamp: new Date().toISOString(),
          redacted: true,
          payload: {
            url,
            method: (rest.method as string) ?? 'GET',
            message: networkError.message,
          },
        });
      }

      throw normalizeNetworkError();
    }
  };

  let response = await doFetch();

  if (response.status === 401 && !isAuthRequest) {
    const refreshed = await refreshSession();

    if (refreshed && refreshed.token) {
      useAuthStore.setState({ token: refreshed.token, refreshToken: refreshed.refreshToken });

      response = await doFetch();
    } else {
      // Refresh falló, hacer logout (refreshSession ya llamó logout, pero por seguridad)
      logout();
      // Lanzar error para detener la ejecución
      throw {
        name: 'ApiError',
        status: 401,
        code: 'SESSION_EXPIRED',
        message: 'La sesión ha caducado.',
      };
    }
  }

  if (!response.ok) {
    const error = await normalizeApiError(response);

    if (isDebugPanelEnabled() && response.status >= 500) {
      useDebugStore.getState().push({
        type: 'api-error',
        id: randomUUID(),
        timestamp: new Date().toISOString(),
        redacted: true,
        payload: {
          url,
          method: (rest.method as string) ?? 'GET',
          status: response.status,
          message: error.message,
          body: redact(error.body),
        },
      });
    }

    throw error;
  }

  return response;
};

const logout = () => {
  if (logoutInProgress) {
    return;
  }
  logoutInProgress = true;
  useAuthStore.getState().logout();
  // Limpiar localStorage directamente para asegurar que no queden tokens
  try {
    localStorage.removeItem('auth-store');
  } catch {
    // ignore
  }
  // Usar query param para que el middleware permita el acceso a /auth/login
  window.location.href = '/auth/login?reason=session_expired';
};

export async function apiFetch(path: string, init?: ApiRequestInit): Promise<void>;
export async function apiFetch<TResponse>(path: string, init?: ApiRequestInit): Promise<TResponse>;
export async function apiFetch<TResponse>(
  path: string,
  init: ApiRequestInit = {},
): Promise<TResponse | void> {
  const response = await request(path, init);

  if (response.status === 204) {
    return;
  }

  return (await response.json()) as TResponse;
}

export const apiFetchRaw = (path: string, init?: ApiRequestInit): Promise<Response> =>
  request(path, init);

const toRequestBody = (body?: unknown): BodyInit =>
  body instanceof FormData ? body : JSON.stringify(body ?? {});

export const apiClient = {
  get: <T>(path: string, init?: ApiRequestInit) => apiFetch<T>(path, { ...init, method: 'GET' }),
  post: <T>(path: string, body?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'POST', body: toRequestBody(body) }),
  put: <T>(path: string, body?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'PUT', body: toRequestBody(body) }),
  patch: <T>(path: string, body?: unknown, init?: ApiRequestInit) =>
    apiFetch<T>(path, { ...init, method: 'PATCH', body: toRequestBody(body) }),
  delete: (path: string, init?: ApiRequestInit) => apiFetch(path, { ...init, method: 'DELETE' }),
};
